import { alpha, Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import type { KeyboardEvent } from "react";
import type { AnalysisCardId } from "src-next/modules/clinical-insights/types/analysisTypes";
import {
  SELECTED_CARD_BG_ALPHA,
  analysisBg,
  analysisBorder,
} from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";
import type { AnalysisCardConfig } from "./analysisCardTypes";

const CARD_ICON_SIZE = 24;
const CARD_DESCRIPTION_INDENT = CARD_ICON_SIZE + 12;

interface AnalysisSelectCardProps {
  card: AnalysisCardConfig;
  isSelected: boolean;
  showBusySpinner?: boolean;
  onSelect: (id: AnalysisCardId) => void;
}

export function AnalysisSelectCard({
  card,
  isSelected,
  showBusySpinner = false,
  onSelect,
}: AnalysisSelectCardProps) {
  const clickable = card.active;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(card.id);
    }
  };

  const cardBody = (
    <Stack
      spacing={0.5}
      onClick={clickable ? () => onSelect(card.id) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
      aria-pressed={clickable ? isSelected : undefined}
      aria-label={card.title}
      sx={{
        minWidth: 0,
        height: "100%",
        px: 1.5,
        py: 1.75,
        minHeight: { xs: 88, sm: 92 },
        borderRadius: 1.5,
        cursor: clickable ? "pointer" : "default",
        opacity: card.active ? 1 : 0.55,
        border: "1px solid",
        borderColor: isSelected ? card.color : "divider",
        borderLeft: "4px solid",
        borderLeftColor: isSelected ? card.color : analysisBorder(card.color, 0.35),
        bgcolor: isSelected ? analysisBg(card.color, SELECTED_CARD_BG_ALPHA) : "background.paper",
        boxShadow: isSelected ? `0 0 0 1px ${alpha(card.color, 0.25)}` : "none",
        transition:
          "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease",
        ...(clickable && {
          "&:hover": {
            bgcolor: isSelected ? analysisBg(card.color, 0.1) : analysisBg(card.color, 0.04),
            borderColor: card.color,
          },
        }),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          component={card.icon}
          sx={{
            fontSize: CARD_ICON_SIZE,
            color: card.active ? card.color : "text.disabled",
            flexShrink: 0,
          }}
        />
        <Typography
          variant="subtitle2"
          fontWeight={isSelected ? 700 : 600}
          color={card.active ? "text.primary" : "text.disabled"}
          sx={{ lineHeight: 1.3, minWidth: 0, flex: 1 }}
        >
          {card.title}
        </Typography>
        {showBusySpinner ? (
          <CircularProgress size={12} sx={{ color: card.color, flexShrink: 0 }} />
        ) : null}
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          pl: `${CARD_DESCRIPTION_INDENT}px`,
          lineHeight: 1.45,
          display: "-webkit-box",
          fontSize: "0.8125rem",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {card.description}
      </Typography>
    </Stack>
  );

  return (
    <Tooltip title={card.description} arrow placement="top" describeChild>
      <Box sx={{ minWidth: 0, height: "100%" }}>{cardBody}</Box>
    </Tooltip>
  );
}
